import React, { useEffect, useMemo, useState } from 'react';
import { SearchBox, Spinner, SpinnerSize } from '@fluentui/react';
import ReactECharts from 'echarts-for-react';
import { PeopleRegular } from '@fluentui/react-icons';

import * as MedicalProtocolsService from '../../../services/MedicalProtocols.Services';
import {
    ProtocolPatientStatusDetail,
    ProtocolPatientStatusStatistics
} from '../../../models/Entities/Protocols/ProtocolPatientStatusStatistics';

import * as Functions from './PatientStatusStatistics.Functions';
import ExcelExportButton from './ExcelExportButton.Component';
import { useEChartsTheme, useStableChartPalette, useThemeMode } from './EChartsTheme';
import { buildFileName, downloadWorkbook, patientStatusSheets } from './StatisticsExcel.Functions';

import './ContractStatistics.Styles.css';
import './PatientStatusStatistics.Styles.css';

interface IProps {
    /** Protocolo a mostrar. Se ignora con AllProtocols. */
    ProtocolId?: number;
    /** Todos los protocolos juntos: la vista de la cartera. */
    AllProtocols?: boolean;
    /** Con AllProtocols: limita la cartera a estos protocolos. Sin ids, son todos. */
    ProtocolIds?: number[];
    /** Con AllProtocols: cómo se nombra el alcance en los textos ("de los protocolos en curso"). */
    ScopeText?: string;
    AccessToken: string | undefined;
    /**
     * Estadísticas ya traídas por quien usa el componente, para no pedirlas dos
     * veces. Con esto presente el componente no hace su propio pedido de cifras.
     */
    PreloadedStatistics?: ProtocolPatientStatusStatistics[];
    PreloadedLoading?: boolean;
    PreloadedFailed?: boolean;
    /** Detalle de pacientes ya traído (la cartera lo usa para su línea de tiempo). */
    PreloadedDetails?: ProtocolPatientStatusDetail[];
    /** Texto para el nombre del archivo Excel. */
    FileBase?: string;
    /** Alcance que se escribe dentro del Excel ("Protocolos ongoing", "Protocolo P-12"). */
    ExportScope?: string;
}

interface ToggleProps {
    open: boolean;
    onClick: () => void;
    openText: string;
    closedText: string;
}

/** Botón de despliegue de las secciones plegables (nomenclador y detalle). */
const SectionToggle: React.FC<ToggleProps> = ({ open, onClick, openText, closedText }) => (
    <button type="button" className="pss-detail__toggle" aria-expanded={open} onClick={onClick}>
        <span className={'pss-detail__chevron' + (open ? ' pss-detail__chevron--open' : '')} aria-hidden="true">
            ›
        </span>
        {open ? openText : closedText}
    </button>
);

/**
 * Estado de los pacientes: cuántos hay en cada estado, agrupados por etapa del
 * estudio, y a pedido quiénes son. De un protocolo, o de toda la cartera.
 *
 * Se pide directo al servicio: son datos de lectura, de esta pantalla sola, y no
 * hay otra parte de la aplicación que los necesite en el store.
 *
 * No depende del contrato. Un protocolo sin contrato asociado igual tiene
 * pacientes, y esta sección se muestra en los dos casos.
 */
export const PatientStatusStatisticsComponent: React.FC<IProps> = ({
    ProtocolId,
    AllProtocols,
    ProtocolIds,
    ScopeText,
    AccessToken,
    PreloadedStatistics,
    PreloadedLoading,
    PreloadedFailed,
    PreloadedDetails,
    FileBase,
    ExportScope
}) => {
    const chart = useStableChartPalette();
    const echartsTheme = useEChartsTheme();
    const themeMode = useThemeMode();
    const usesPreloaded = PreloadedStatistics !== undefined || PreloadedLoading !== undefined;
    const hasScope = !!AllProtocols || !!ProtocolId;
    const scopeIds = AllProtocols ? ProtocolIds ?? [] : [ProtocolId as number];

    const [fetchedStatistics, setFetchedStatistics] = useState<ProtocolPatientStatusStatistics[] | undefined>(undefined);
    const [fetchLoading, setFetchLoading] = useState<boolean>(false);
    const [fetchFailed, setFetchFailed] = useState<boolean>(false);

    // Nomenclador desplegable
    const [isCatalogOpen, setIsCatalogOpen] = useState<boolean>(false);

    // Detalle desplegable
    const [isDetailOpen, setIsDetailOpen] = useState<boolean>(false);
    const [fetchedDetails, setFetchedDetails] = useState<ProtocolPatientStatusDetail[] | undefined>(undefined);
    const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false);
    const [failedDetail, setFailedDetail] = useState<boolean>(false);
    const [selectedStatus, setSelectedStatus] = useState<number | undefined>(undefined);
    const [searchText, setSearchText] = useState<string>('');

    const details = PreloadedDetails ?? fetchedDetails;

    // Otro alcance: lo cargado del anterior ya no sirve.
    useEffect(() => {
        setFetchedDetails(undefined);
        setIsDetailOpen(false);
        setSelectedStatus(undefined);
        setSearchText('');
    }, [ProtocolId, AllProtocols]);

    useEffect(() => {
        if (usesPreloaded || !AccessToken || !hasScope) return;

        let cancelled = false;
        setFetchLoading(true);
        setFetchFailed(false);

        // Sin ids, la API devuelve todos los protocolos con pacientes.
        MedicalProtocolsService.GetProtocolPatientStatusStatistics(AllProtocols ? [] : [ProtocolId as number], AccessToken)
            .then((response) => {
                if (!cancelled) setFetchedStatistics(response ?? []);
            })
            .catch(() => {
                if (!cancelled) setFetchFailed(true);
            })
            .finally(() => {
                if (!cancelled) setFetchLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [usesPreloaded, AccessToken, ProtocolId, AllProtocols, hasScope]);

    /** Detalle filtrado al alcance: la API puede devolver más protocolos de los pedidos. */
    const scopeDetails = (response: ProtocolPatientStatusDetail[] | undefined): ProtocolPatientStatusDetail[] =>
        AllProtocols
            ? (response ?? []).filter((item) => !ProtocolIds || ProtocolIds.includes(item.protocolId))
            : (response ?? []).filter((item) => item.protocolId === ProtocolId);

    /**
     * El detalle se pide recién la primera vez que se despliega. Es la lista entera
     * de pacientes —en la cartera, todos los de todos los protocolos— y la mayoría
     * de las veces alcanza con las cifras. Una vez traída se conserva mientras no
     * cambie el alcance.
     */
    useEffect(() => {
        if (!isDetailOpen || details !== undefined || !AccessToken || !hasScope) return;

        let cancelled = false;
        setIsLoadingDetail(true);
        setFailedDetail(false);

        MedicalProtocolsService.GetProtocolPatientStatusDetail(scopeIds, AccessToken)
            .then((response) => {
                if (!cancelled) setFetchedDetails(scopeDetails(response));
            })
            .catch(() => {
                if (!cancelled) setFailedDetail(true);
            })
            .finally(() => {
                if (!cancelled) setIsLoadingDetail(false);
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDetailOpen, details, AccessToken, ProtocolId, AllProtocols, hasScope]);

    const sourceStatistics = usesPreloaded ? PreloadedStatistics : fetchedStatistics;
    const isLoading = usesPreloaded ? !!PreloadedLoading : fetchLoading;
    const failed = usesPreloaded ? !!PreloadedFailed : fetchFailed;

    // La API responde una lista por protocolo aunque se pida uno solo.
    const statistics = useMemo(
        () =>
            AllProtocols
                ? Functions.mergeStatistics(sourceStatistics)
                : (sourceStatistics ?? []).find((item) => item.protocolId === ProtocolId),
        [AllProtocols, sourceStatistics, ProtocolId]
    );

    const rows = useMemo(() => Functions.buildStatusRows(statistics), [statistics]);
    const total = statistics?.total ?? rows.reduce((acc, row) => acc + row.count, 0);
    const stageGroups = useMemo(() => Functions.buildStageGroups(rows, total), [rows, total]);
    const catalog = useMemo(() => Functions.buildStatusCatalog(statistics), [statistics]);
    const visibleDetails = useMemo(
        () =>
            Functions.sortDetailsByName(
                Functions.searchDetails(Functions.filterDetailByStatus(details ?? [], selectedStatus), searchText)
            ),
        [details, selectedStatus, searchText]
    );

    const scopeText = AllProtocols ? ScopeText ?? 'de todos los protocolos' : 'del protocolo';

    /**
     * Excel con todo el detalle: si la lista de pacientes todavía no se trajo, se
     * pide antes de armar el archivo y se deja cargada para el desplegable.
     */
    const handleExport = async () => {
        let exportDetails = details;
        if (exportDetails === undefined) {
            if (!AccessToken) throw new Error('Sin token');
            exportDetails = scopeDetails(await MedicalProtocolsService.GetProtocolPatientStatusDetail(scopeIds, AccessToken));
            setFetchedDetails(exportDetails);
        }
        const base = FileBase ?? (AllProtocols ? 'estado-de-los-pacientes-cartera' : `estado-de-los-pacientes-protocolo-${ProtocolId}`);
        await downloadWorkbook(buildFileName(base), patientStatusSheets(statistics, exportDetails, !!AllProtocols), {
            title: 'Estado de los pacientes',
            scope: ExportScope ?? (AllProtocols ? 'Todos los protocolos' : `Protocolo #${ProtocolId}`)
        });
    };

    /** Tocar un estado en la tabla abre el detalle ya filtrado por ese estado. */
    const openDetailForStatus = (status: number) => {
        setSelectedStatus(selectedStatus === status && isDetailOpen ? undefined : status);
        setIsDetailOpen(true);
    };

    const barOption = useMemo(() => {
        if (rows.length === 0) return null;
        // Invertidas para el eje: ECharts dibuja la primera categoría abajo, y así la
        // etapa de selección queda arriba, en el mismo orden que la tabla.
        const ordered = rows.slice().reverse();
        return {
            grid: { left: 12, right: 64, top: 8, bottom: 8, containLabel: true },
            tooltip: {
                trigger: 'item',
                formatter: (params: any) => {
                    const row = ordered[params.dataIndex];
                    return `${params.name}<br/><b>${params.value}</b> pacientes · ${row.percentage}%<br/>${Functions.STAGE_LABEL[row.stage]}`;
                }
            },
            xAxis: { type: 'value', minInterval: 1, splitLine: { lineStyle: { color: chart.gridLine } } },
            yAxis: {
                type: 'category',
                data: ordered.map((row) => row.label),
                axisTick: { show: false },
                axisLine: { lineStyle: { color: chart.axisLine } }
            },
            series: [
                {
                    type: 'bar',
                    barMaxWidth: 26,
                    data: ordered.map((row) => ({
                        value: row.count,
                        itemStyle: { color: Functions.statusColor(row.status, themeMode), borderRadius: [0, 4, 4, 0] }
                    })),
                    label: {
                        show: true,
                        position: 'right',
                        formatter: (params: any) => `${params.value} · ${ordered[params.dataIndex].percentage}%`,
                        color: chart.textSecondary,
                        fontSize: 11
                    }
                }
            ]
        };
    }, [rows, chart, themeMode]);

    return (
        <div className="cs-card">
            <div className="cs-card__header cs-card__header--actions">
                <div className="cs-card__header-text">
                    <div className="cs-card__title">Estado de los pacientes</div>
                    <div className="cs-card__desc">
                        Cantidad de pacientes {scopeText} en cada estado, agrupados por etapa. Desplegá el detalle para ver quiénes son.
                    </div>
                </div>
                <ExcelExportButton onExport={handleExport} disabled={isLoading || failed || rows.length === 0} />
            </div>

            {isLoading && (
                <div className="cs-loading cs-loading--compact">
                    <Spinner size={SpinnerSize.small} />
                    <span>Cargando estado de los pacientes…</span>
                </div>
            )}

            {!isLoading && failed && (
                <div className="cs-empty">
                    <PeopleRegular className="cs-empty__icon" />
                    <span>No se pudo obtener el estado de los pacientes.</span>
                    <span className="cs-empty__hint">Volvé a abrir la solapa para reintentar.</span>
                </div>
            )}

            {!isLoading && !failed && rows.length === 0 && (
                <div className="cs-empty">
                    <PeopleRegular className="cs-empty__icon" />
                    <span>
                        {AllProtocols
                            ? 'Ningún protocolo tiene todavía pacientes con estado registrado.'
                            : 'Este protocolo todavía no tiene pacientes con estado registrado.'}
                    </span>
                </div>
            )}

            {!isLoading && !failed && rows.length > 0 && (
                <>
                    {/* Total y etapas. Cada etapa es un bloque con su subtotal y las
                        celdas de sus estados adentro: la agrupación se ve antes de
                        leer ningún número. */}
                    <div className="pss-stages">
                        <div className="pss-stage pss-stage--total">
                            <div className="pss-stage__header">
                                <span className="pss-stage__title">Pacientes</span>
                                <PeopleRegular className="pss-stage__icon" />
                            </div>
                            <div className="pss-stage__count">{total}</div>
                            <div className="pss-stage__sub">
                                {AllProtocols ? `Suma ${scopeText}` : 'Total informado para el protocolo'}
                            </div>
                        </div>

                        {stageGroups.map((group) => (
                            <div key={group.stage} className={`pss-stage pss-stage--${group.stage}`}>
                                <div className="pss-stage__header">
                                    <span className="pss-stage__title">{group.label}</span>
                                    <span className="pss-stage__total">
                                        {group.count} · {group.percentage}%
                                    </span>
                                </div>
                                {/* Fallas de selección tiene un único estado posible
                                    (ScreenFailure): la cifra de la etapa ya lo dice todo,
                                    y una celda adentro sólo la repetiría. */}
                                {group.stage === 'screenFailure' ? (
                                    <div className="pss-stage__count">{group.count}</div>
                                ) : group.rows.length === 0 ? (
                                    <div className="pss-stage__empty">Sin pacientes en esta etapa</div>
                                ) : (
                                    <div className="pss-stage__cells">
                                        {group.rows.map((row) => (
                                            <div key={row.status} className="pss-stage__cell">
                                                <div
                                                    className="pss-kpi-cell__accent"
                                                    style={{ background: Functions.statusColor(row.status, themeMode) }}
                                                    aria-hidden="true"
                                                />
                                                <div className="cs-kpi-cell__value">{row.count}</div>
                                                <div className="cs-kpi-cell__label">{row.label}</div>
                                                <div className="cs-kpi-cell__sub">{row.percentage}% del total</div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    <div className="cs-card__body">
                        <div className="cs-two-col">
                            <div>
                                <div className="cs-cf-chart-title">Pacientes por estado</div>
                                {barOption && (
                                    <ReactECharts
                                        theme={echartsTheme}
                                        option={barOption}
                                        style={{ height: Math.max(180, rows.length * 42) }}
                                        opts={{ renderer: 'svg' }}
                                    />
                                )}
                            </div>

                            <div>
                                <div className="cs-cf-chart-title">Detalle por estado</div>
                                <div className="cs-table-wrap">
                                    <table className="cs-table">
                                        <thead>
                                            <tr>
                                                <th>Estado</th>
                                                <th>Pacientes</th>
                                                <th>% del total</th>
                                            </tr>
                                        </thead>
                                        {stageGroups.map((group) => (
                                            <tbody key={group.stage}>
                                                <tr className={`pss-stage-row pss-stage-row--${group.stage}`}>
                                                    <td>{group.label}</td>
                                                    <td>{group.count}</td>
                                                    <td>{group.percentage}%</td>
                                                </tr>
                                                {group.rows.map((row) => (
                                                    <tr
                                                        key={row.status}
                                                        className={
                                                            'pss-row' +
                                                            (isDetailOpen && selectedStatus === row.status
                                                                ? ' pss-row--selected'
                                                                : '')
                                                        }
                                                        onClick={() => openDetailForStatus(row.status)}
                                                        title="Ver los pacientes de este estado"
                                                    >
                                                        <td className="pss-row__status">
                                                            <span className="pss-dot" style={{ background: Functions.statusColor(row.status, themeMode) }} />
                                                            {row.label}
                                                        </td>
                                                        <td>{row.count}</td>
                                                        <td>{row.percentage}%</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        ))}
                                    </table>
                                </div>
                            </div>
                        </div>

                        <div className="pss-detail">
                            <SectionToggle
                                open={isCatalogOpen}
                                onClick={() => setIsCatalogOpen(!isCatalogOpen)}
                                openText="Ocultar nomenclador de estados"
                                closedText="Ver nomenclador de estados"
                            />

                            {isCatalogOpen && (
                                <>
                                    <div className="pss-catalog__note">
                                        Glosario de estados del paciente en el protocolo. El nombre informado es el que muestra el sistema cuando el estado tiene pacientes.
                                    </div>
                                    <div className="cs-table-wrap pss-catalog__table">
                                        <table className="cs-table">
                                            <thead>
                                                <tr>
                                                    <th>Código</th>
                                                    <th>Estado</th>
                                                    <th>Nombre informado</th>
                                                    <th>Etapa</th>
                                                    <th>{AllProtocols ? 'Pacientes' : 'Pacientes en este protocolo'}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {catalog.map((entry) => (
                                                    <tr
                                                        key={entry.status}
                                                        className={entry.countInProtocol === 0 ? 'pss-catalog__unused' : undefined}
                                                    >
                                                        <td>{entry.status}</td>
                                                        <td>
                                                            <span className="pss-dot" style={{ background: Functions.statusColor(entry.status, themeMode) }} />
                                                            {entry.name}
                                                        </td>
                                                        <td>{entry.description ?? '—'}</td>
                                                        <td>
                                                            <span className={`pss-stage-tag pss-stage-tag--${entry.stage}`}>
                                                                {Functions.STAGE_LABEL[entry.stage]}
                                                            </span>
                                                        </td>
                                                        <td>{entry.countInProtocol}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="pss-detail">
                            <SectionToggle
                                open={isDetailOpen}
                                onClick={() => setIsDetailOpen(!isDetailOpen)}
                                openText="Ocultar detalle de pacientes"
                                closedText="Ver detalle de pacientes"
                            />

                            {isDetailOpen && isLoadingDetail && (
                                <div className="cs-loading cs-loading--compact">
                                    <Spinner size={SpinnerSize.small} />
                                    <span>Cargando pacientes…</span>
                                </div>
                            )}

                            {isDetailOpen && !isLoadingDetail && failedDetail && (
                                <div className="cs-empty cs-empty--compact">
                                    <span>No se pudo obtener el detalle de los pacientes.</span>
                                    <button
                                        type="button"
                                        className="pss-detail__toggle"
                                        onClick={() => {
                                            setFailedDetail(false);
                                            setFetchedDetails(undefined);
                                        }}
                                    >
                                        Reintentar
                                    </button>
                                </div>
                            )}

                            {isDetailOpen && !isLoadingDetail && !failedDetail && details !== undefined && (
                                <>
                                    <div className="pss-detail__header">
                                        <SearchBox
                                            className="pss-detail__search"
                                            placeholder="Buscar por nombre o N° en protocolo"
                                            underlined={false}
                                            value={searchText}
                                            onChange={(_, newValue) => setSearchText(newValue || '')}
                                            onClear={() => setSearchText('')}
                                        />
                                        {/* Filtro por estado, agrupado por etapa como el resto de
                                            la sección. Está además del clic en la tabla porque, con
                                            la lista abierta, la tabla puede quedar fuera de la vista. */}
                                        <div className="pss-filter">
                                            <button
                                                type="button"
                                                className={
                                                    'pss-chip' + (selectedStatus === undefined ? ' pss-chip--active' : '')
                                                }
                                                onClick={() => setSelectedStatus(undefined)}
                                            >
                                                Todos
                                            </button>
                                            {stageGroups
                                                .filter((group) => group.rows.length > 0)
                                                .map((group) => (
                                                    <div key={group.stage} className="pss-filter__group">
                                                        <span className="pss-filter__group-label">{group.label}</span>
                                                        {group.rows.map((row) => (
                                                            <button
                                                                type="button"
                                                                key={row.status}
                                                                className={
                                                                    'pss-chip' +
                                                                    (selectedStatus === row.status ? ' pss-chip--active' : '')
                                                                }
                                                                onClick={() => setSelectedStatus(row.status)}
                                                            >
                                                                <span className="pss-dot" style={{ background: Functions.statusColor(row.status, themeMode) }} />
                                                                {row.label}
                                                            </button>
                                                        ))}
                                                    </div>
                                                ))}
                                        </div>
                                        <span className="pss-detail__count">
                                            {visibleDetails.length} de {details.length} pacientes
                                        </span>
                                    </div>

                                    {visibleDetails.length === 0 ? (
                                        <div className="cs-empty cs-empty--compact">
                                            <span>
                                                {searchText.trim()
                                                    ? 'Ningún paciente coincide con la búsqueda.'
                                                    : 'No hay pacientes en este estado.'}
                                            </span>
                                        </div>
                                    ) : (
                                        <div className="cs-table-wrap pss-detail__table">
                                            <table className="cs-table">
                                                <thead>
                                                    <tr>
                                                        <th>Paciente</th>
                                                        {AllProtocols && <th>Protocolo</th>}
                                                        <th>N° en protocolo</th>
                                                        <th>Estado</th>
                                                        <th>Etapa</th>
                                                        <th>Randomización</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {visibleDetails.map((item, index) => {
                                                        const stage = Functions.statusStage(item.status);
                                                        return (
                                                            <tr
                                                                key={`${item.protocolId}-${item.medicalRecordNumber ?? ''}-${item.numberInProtocol ?? ''}-${index}`}
                                                            >
                                                                <td>{item.patientFullName || '—'}</td>
                                                                {AllProtocols && <td>{item.protocolNumber || `#${item.protocolId}`}</td>}
                                                                <td>{item.numberInProtocol || '—'}</td>
                                                                <td>
                                                                    <span
                                                                        className="pss-dot"
                                                                        style={{ background: Functions.statusColor(item.status, themeMode) }}
                                                                    />
                                                                    {Functions.statusLabel(item.status, item.statusDescription)}
                                                                </td>
                                                                <td>
                                                                    <span className={`pss-stage-tag pss-stage-tag--${stage}`}>
                                                                        {Functions.STAGE_LABEL[stage]}
                                                                    </span>
                                                                </td>
                                                                <td>{Functions.formatRandomizationDate(item.randomizationDate)}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default PatientStatusStatisticsComponent;
